import { useState, useEffect, useRef } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import './index.scss';
import type { MenuProps } from 'antd';
import { Menu, message, Button, Dropdown, Avatar, Result, Spin } from 'antd';
import NotificationIcon from '@/assets/alert.svg';
import wavvestLogoIcon from '@/assets/login/wavvest-logo-icon.svg';
import wavvestLogoText from '@/assets/login/wavvest-logo-text.svg';
import { useAuth } from '@/contexts/AuthContext';
import { postAuth } from '@/util/util';
import { FormModalProvider } from '@/common/components/modal/context';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggleIcon } from '@/common/components/themed';
import { selectNormalBrandLogo, selectCompactBrandLogo } from '@wavvest/shared-utils';
import {
  getRiaProfile,
  InvestorApiReadiness,
  getInvestorApiRuntimeContext,
  getInvestorApiReadiness,
  getVerificationResult,
  getVerificationStatus,
} from '@/api/custom';
import { getGlobalFlag } from '@/api/feature-flag-api';
import PlaidLinkModal from '@/pages/wealth/components/PlaidLinkModal';
import { onPlaidLinkRequest } from '@/pages/wealth/components/plaid-link-request';
// import { isInternalFeatureEnabled } from "@/util/featureGate";
import { useAppSelector } from '@/store/hooks';
import { useDispatch } from 'react-redux';
import {
  setRiaProfile,
  selectRiaProfile,
  selectUser,
} from '@/store/features/userinfo/userinfoSlice';
import { setKycResult, setVirifiedStatus } from '@/store/features/plaid/identitySlice';
import {
  PoweroffOutlined,
  IdcardOutlined,
  HomeOutlined,
  BarChartOutlined,
  FolderOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { captureException, captureMessage } from '@/sentry';
import { getBrandedLoginPath } from '@/pages/signup/brandContext';

const WEALTH_PREPARATION_GRACE_MS = 30000;
const INVESTOR_READINESS_LOG_EVERY_N_POLLS = 6;
const MAX_READINESS_POLL_ATTEMPTS = 24;
const HOME_READINESS_EXCEPTION_SESSION_KEY = 'home_investor_readiness_exception_captured';

const Home: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const { logout } = useAuth();
  const [, setLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [plaidModalVisible, setPlaidModalVisible] = useState(false);
  const [textLogoError, setTextLogoError] = useState(false);
  const globalRiaProfile = useAppSelector(selectRiaProfile);
  const user = useAppSelector(selectUser);
  const [riaProfileLoaded, setRiaProfileLoaded] = useState(false);
  const [kycLoaded, setKycLoaded] = useState(false);
  const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState(false);
  const [investorApiReadiness, setInvestorApiReadiness] = useState<InvestorApiReadiness>({
    ready: false,
    plaidReady: false,
    identityReady: false,
  });
  const readinessRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preparationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackActiveRef = useRef(false);
  const [preparationGraceExpired, setPreparationGraceExpired] = useState(false);
  const readinessNotReadyPollCountRef = useRef(0);
  const readinessExceptionCapturedRef = useRef(false);
  const readinessRouteRef = useRef(location.pathname);
  const readinessIdentityRef = useRef({
    userId: user?.userId || null,
    cognitoId: user?.cognito_id || null,
  });

  // Mobile bottom menu
  const navContentRef = useRef<HTMLDivElement>(null);
  const [showBottomMenu, setShowBottomMenu] = useState(true);
  const lastScrollTop = useRef(0);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isWealthRoute = /^\/home\/wealth\/?$/.test(location.pathname);

  useEffect(() => onPlaidLinkRequest(() => setPlaidModalVisible(true)), []);

  useEffect(() => {
    readinessRouteRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    readinessIdentityRef.current = {
      userId: user?.userId || null,
      cognitoId: user?.cognito_id || null,
    };
  }, [user?.userId, user?.cognito_id]);

  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || 'U';
    return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase() || 'U';
  };

  const userNameSeparator = (username: string) => {
    if (!username) return { firstName: '', lastName: '' };
    const parts = username.trim().split(' ');
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const isMobile = windowSize.width <= 768;

  const setRiaProfileToStore = async () => {
    try {
      const riaProfile = await getRiaProfile();
      dispatch(setRiaProfile(riaProfile));
    } finally {
      setRiaProfileLoaded(true);
    }
  };

  useEffect(() => {
    postAuth();
    setRiaProfileToStore();
    getGlobalFlag('maintenance-flag')
      .then((flag) => {
        const normalizedFlagValue = flag.value.trim().toLowerCase();
        const isEnabledValue = normalizedFlagValue === 'enabled';
        setMaintenanceModeEnabled(isEnabledValue);
      })
      .catch((error) => {
        console.error('Error loading maintenance feature flag:', error);
        setMaintenanceModeEnabled(false);
      });
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const clearReadinessRetryTimer = () => {
      if (readinessRetryTimeoutRef.current) {
        clearTimeout(readinessRetryTimeoutRef.current);
        readinessRetryTimeoutRef.current = null;
      }
    };

    const shouldCaptureReadinessException = (): boolean => {
      if (readinessExceptionCapturedRef.current) {
        return false;
      }
      readinessExceptionCapturedRef.current = true;
      try {
        if (sessionStorage.getItem(HOME_READINESS_EXCEPTION_SESSION_KEY) === '1') {
          return false;
        }
        sessionStorage.setItem(HOME_READINESS_EXCEPTION_SESSION_KEY, '1');
      } catch {
        return true;
      }
      return true;
    };

    const checkReadiness = async () => {
      try {
        const readiness = await getInvestorApiReadiness();
        if (!active) {
          return;
        }
        setInvestorApiReadiness(readiness);

        if (!readiness.ready) {
          readinessNotReadyPollCountRef.current += 1;
          const pollCount = readinessNotReadyPollCountRef.current;
          if (pollCount === 1 || pollCount % INVESTOR_READINESS_LOG_EVERY_N_POLLS === 0) {
            const runtimeContext = getInvestorApiRuntimeContext();
            captureMessage(
              `[InvestorReadiness][Home] route=${readinessRouteRef.current} poll_count=${pollCount} reason=plaidReady:${readiness.plaidReady},identityReady:${readiness.identityReady} user_id=${readinessIdentityRef.current.userId || 'unknown'} cognito_id=${readinessIdentityRef.current.cognitoId || 'unknown'} plaid_alias=${runtimeContext.resolvedPlaidAlias || 'none'} identity_alias=${runtimeContext.resolvedIdentityAlias || 'none'} available_aliases=${runtimeContext.availableApiAliases.join('|') || 'none'}`,
              'warning',
            );
          }
          if (pollCount >= MAX_READINESS_POLL_ATTEMPTS) {
            clearReadinessRetryTimer();
            return;
          }
          clearReadinessRetryTimer();
          readinessRetryTimeoutRef.current = setTimeout(() => {
            void checkReadiness();
          }, 5000);
        } else {
          readinessNotReadyPollCountRef.current = 0;
          clearReadinessRetryTimer();
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setInvestorApiReadiness({
          ready: false,
          plaidReady: false,
          identityReady: false,
        });
        readinessNotReadyPollCountRef.current += 1;
        const pollCount = readinessNotReadyPollCountRef.current;
        if (shouldCaptureReadinessException()) {
          const runtimeContext = getInvestorApiRuntimeContext();
          const normalizedError = error instanceof Error ? error : new Error(String(error));
          captureException(normalizedError, {
            route: readinessRouteRef.current,
            userId: readinessIdentityRef.current.userId,
            cognitoId: readinessIdentityRef.current.cognitoId,
            pollCount,
            reason: 'readiness_check_exception',
            plaidAlias: runtimeContext.resolvedPlaidAlias,
            identityAlias: runtimeContext.resolvedIdentityAlias,
            availableApiAliases: runtimeContext.availableApiAliases,
          });
        }
        if (pollCount === 1 || pollCount % INVESTOR_READINESS_LOG_EVERY_N_POLLS === 0) {
          const runtimeContext = getInvestorApiRuntimeContext();
          captureMessage(
            `[InvestorReadiness][Home] route=${readinessRouteRef.current} poll_count=${pollCount} reason=exception user_id=${readinessIdentityRef.current.userId || 'unknown'} cognito_id=${readinessIdentityRef.current.cognitoId || 'unknown'} plaid_alias=${runtimeContext.resolvedPlaidAlias || 'none'} identity_alias=${runtimeContext.resolvedIdentityAlias || 'none'} available_aliases=${runtimeContext.availableApiAliases.join('|') || 'none'}`,
            'warning',
          );
        }
        if (pollCount >= MAX_READINESS_POLL_ATTEMPTS) {
          clearReadinessRetryTimer();
          return;
        }
        clearReadinessRetryTimer();
        readinessRetryTimeoutRef.current = setTimeout(() => {
          void checkReadiness();
        }, 5000);
      }
    };

    void checkReadiness();

    return () => {
      active = false;
      clearReadinessRetryTimer();
    };
  }, []);

  useEffect(() => {
    const clearPreparationTimer = () => {
      if (preparationTimeoutRef.current) {
        clearTimeout(preparationTimeoutRef.current);
        preparationTimeoutRef.current = null;
      }
    };

    if (!isWealthRoute) {
      setPreparationGraceExpired(false);
      clearPreparationTimer();
      return;
    }

    if (investorApiReadiness.ready) {
      setPreparationGraceExpired(false);
      clearPreparationTimer();
      return;
    }

    if (preparationGraceExpired) {
      return;
    }

    if (!preparationTimeoutRef.current) {
      preparationTimeoutRef.current = setTimeout(() => {
        setPreparationGraceExpired(true);
        preparationTimeoutRef.current = null;
      }, WEALTH_PREPARATION_GRACE_MS);
    }

    return () => {
      clearPreparationTimer();
    };
  }, [isWealthRoute, investorApiReadiness.ready, preparationGraceExpired]);

  useEffect(() => {
    if (!isWealthRoute) {
      fallbackActiveRef.current = false;
      return;
    }

    if (!investorApiReadiness.ready) {
      if (!fallbackActiveRef.current) {
        fallbackActiveRef.current = true;
        captureMessage(
          `[InvestorFallback] Wealth fallback active (plaidReady=${investorApiReadiness.plaidReady}, identityReady=${investorApiReadiness.identityReady})`,
          'warning',
        );
      }
      return;
    }

    if (fallbackActiveRef.current) {
      fallbackActiveRef.current = false;
      captureMessage('[InvestorFallback] Wealth fallback recovered', 'info');
    }
  }, [
    isWealthRoute,
    investorApiReadiness.ready,
    investorApiReadiness.plaidReady,
    investorApiReadiness.identityReady,
  ]);

  useEffect(() => {
    if (location.pathname === '/home/customer-financial-plan') {
      setTitle('Customers Financial plan');
    } else if (location.pathname === '/home/financial-plan-edit') {
      setTitle('Edit Financial Plan');
    } else if (isWealthRoute) {
      const firstName = userNameSeparator(user?.name ?? '').firstName;
      setTitle(firstName ? `Hi, ${firstName}` : 'Wealth Dashboard');
    } else {
      const displayTitle = displayTitles[location.pathname];
      if (displayTitle) {
        setTitle(displayTitle);
      } else {
        const menuItem = items.find((item) => item.key === location.pathname);
        setTitle(menuItem?.label || 'Default Title');
      }
    }
  }, [location.pathname, isWealthRoute]);

  useEffect(() => {
    const outletContainer = document.querySelector('.outletContainer');
    if (outletContainer) {
      localStorage.setItem('outletHeight', outletContainer.clientHeight.toString());
    }
    const home = document.getElementById('home');
    if (home) {
      home.style.width = `${windowSize.width}px`;
      home.style.height = `${windowSize.height}px`;
    }
  }, [windowSize]);

  type MenuItem = {
    key: string;
    label: string;
  };

  const displayTitles: Record<string, string> = {
    // "/home/dashboard": "Dashboard Overview",
    // "/home/wealth" handled in useEffect with "Welcome, {user name}"
    '/home/planning': 'Financial Planning',
    '/home/filehub': 'File Sharing',
    '/home/tax': 'Taxes',
    '/home/insurance': 'Insurance',
    // "/home/messages": "Secure Messages",
    '/home/deposit': 'Deposit',
    '/home/withdraw': 'Withdraw',
    '/home/transfer': 'Transfer',
    '/home/bank-link': 'Choose a bank for your wallet',
    '/home/wealth-profile': 'Wealth Profile',
  };

  const items: MenuItem[] = [
    // { key: "/home/dashboard", label: "Dashboard" },
    { key: '/home/wealth', label: 'Wealth' },
    { key: '/home/planning', label: 'Planning' },
    { key: '/home/filehub', label: 'Files' },
    { key: '/home/tax', label: 'Taxes' },
    { key: '/home/insurance', label: 'Insurance' },
    // { key: "/home/messages", label: "Messages" },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
    const displayTitle = displayTitles[e.key];
    if (displayTitle) {
      setTitle(displayTitle);
    } else {
      const clickedItem = items.find((item) => item.key === e.key);
      if (clickedItem) {
        setTitle(clickedItem.label);
      }
    }
  };

  const logoutClick = async () => {
    setLoading(true);
    try {
      await logout();
      navigate(getBrandedLoginPath());
    } catch (error) {
      messageApi.error('Login failed');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      key: 'wealth-profile',
      label: (
        <div className="dropdown-item-content">
          <IdcardOutlined className="dropdown-icon" />
          <span>Wealth Profile</span>
        </div>
      ),
      onClick: () => navigate('/home/wealth-profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'theme',
      label: (
        <div className="dropdown-item-content">
          <span className="dropdown-icon">
            <ThemeToggleIcon />
          </span>
          <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </div>
      ),
      onClick: toggleTheme,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: (
        <div className="dropdown-item-content">
          <PoweroffOutlined className="dropdown-icon" />
          <span>Log Out</span>
        </div>
      ),
      onClick: logoutClick,
    },
  ];

  const handlePlaidSuccess = () => {
    // PlaidLinkModal refreshes the Plaid snapshot before it calls this callback.
    // Keep the current React tree mounted so /home/wealth does not flash through
    // another full app load after the first external account link.
    setPlaidModalVisible(false);
  };

  const shouldShowApexActions =
    riaProfileLoaded &&
    Boolean(globalRiaProfile.profile?.apex_correspondent_id) &&
    Boolean(globalRiaProfile.features?.apex_enabled);

  const loadKycResult = async () => {
    try {
      const result = await getVerificationResult();
      if (result) {
        const { name, date_of_birth, tax_id, address, phone_number } = result;
        dispatch(
          setKycResult({
            firstName: name.value.first_name,
            lastName: name.value.last_name,
            dateOfBirth: new Date(date_of_birth.value),
            phoneNumber: phone_number.value,
            taxId: tax_id.value,
            personalAddress: {
              line1: address.value.street,
              line2: address.value.street2 || '',
              city: address.value.city,
              state: address.value.region,
              postalCode: address.value.postal_code,
            },
          }),
        );
        setKycLoaded(true);
      }
    } catch (error) {
      console.error(`Error loading KYC result: ${JSON.stringify(error)}`);
    }
  };

  const handleOpenBrokerageAccount = async () => {
    try {
      const response = await getVerificationStatus();
      const isVerified = response?.status === 'success';
      dispatch(setVirifiedStatus(isVerified));

      if (isVerified) {
        // if verified, load KYC result and redirect
        await loadKycResult();
        navigate('/wealth/open-brokerage-account/step1');
      } else {
        // if not verified, redirect to KYC page
        navigate('/kyc');
      }
    } catch (error) {
      console.error('Failed to fetch KYC Status: ', error);
    }
  };

  // Load KYC once after identity + apex readiness so we avoid reruns/redirect churn;
  // `kycLoaded` is intentionally excluded from deps to keep this as a one-time readiness-triggered fetch.
  useEffect(() => {
    // only try to load KYC result when the component mounts, without triggering any redirect
    if (investorApiReadiness.identityReady && shouldShowApexActions && !kycLoaded) {
      loadKycResult();
    }
  }, [investorApiReadiness.identityReady, shouldShowApexActions]);

  const shouldShowPreparationState =
    isWealthRoute && !investorApiReadiness.ready && !preparationGraceExpired;

  // Mobile bottom menu items with icons
  const mobileMenuItems = [
    { key: '/home/wealth', icon: <HomeOutlined /> },
    { key: '/home/planning', icon: <BarChartOutlined /> },
    { key: '/home/filehub', icon: <FolderOutlined /> },
    { key: '/home/tax', icon: <FileTextOutlined /> },
    { key: '/home/insurance', icon: <SafetyCertificateOutlined /> },
  ];

  // Scroll-based show/hide for mobile bottom menu
  useEffect(() => {
    if (!isMobile) return;
    const el = navContentRef.current;
    if (!el) return;

    const onScroll = () => {
      const currentScrollTop = el.scrollTop;
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      if (currentScrollTop > lastScrollTop.current && currentScrollTop > 50) {
        setShowBottomMenu(false);
      } else {
        setShowBottomMenu(true);
      }

      scrollTimeout.current = setTimeout(() => {
        setShowBottomMenu(true);
      }, 150);

      lastScrollTop.current = currentScrollTop;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [isMobile]);

  const selectedKey = location.pathname;
  const containerClass = `home-container ${theme}-theme`;
  const darkTextLogoUri = globalRiaProfile?.profile?.text_logo_uri || '';
  const lightTextLogoUri = globalRiaProfile?.profile?.light_text_logo_uri || '';
  const themeAwareTextLogoUri =
    theme === 'light' ? lightTextLogoUri || darkTextLogoUri : darkTextLogoUri || lightTextLogoUri;
  // Top navigation: web uses the normal logo, mobile uses the compact logo.
  // Both are theme-aware and fall back to the firm's text logo (for firms that
  // have only configured the legacy text logo) before the Wavvest default.
  const headerProfile = globalRiaProfile?.profile ?? {};
  const normalHeaderLogoUri = selectNormalBrandLogo(headerProfile, theme) || themeAwareTextLogoUri;
  const compactHeaderLogoUri = selectCompactBrandLogo(headerProfile, theme) || normalHeaderLogoUri;
  const textLogoUri = isMobile ? compactHeaderLogoUri : normalHeaderLogoUri;
  const hasResolvedHeaderLogo = riaProfileLoaded || Boolean(globalRiaProfile?.profile);
  const shouldShowTextLogo = Boolean(textLogoUri && !textLogoError);
  const shouldShowLogoFallback = hasResolvedHeaderLogo && !shouldShowTextLogo;

  useEffect(() => {
    setTextLogoError(false);
  }, [textLogoUri]);

  return (
    <div className={containerClass} id="home">
      {contextHolder}
      <div className="navibar">
        <div className="navbarContent">
          <div className="logoContainer" onClick={() => navigate('/home')}>
            {shouldShowTextLogo ? (
              <img
                src={textLogoUri}
                alt={globalRiaProfile?.profile?.name || 'Client Portal'}
                className="login-brand-image login-brand-logo"
                onError={() => setTextLogoError(true)}
              />
            ) : shouldShowLogoFallback ? (
              <div className="login-brand-row">
                <img src={wavvestLogoIcon} alt="" className="login-brand-mark" aria-hidden="true" />
                <img src={wavvestLogoText} alt="Wavvest" className="login-brand-wordmark" />
              </div>
            ) : (
              <div className="text-logo-placeholder" aria-hidden="true" />
            )}
          </div>

          <div className="rightSection">
            <Menu
              selectedKeys={[selectedKey]}
              mode="horizontal"
              items={items}
              onClick={handleMenuClick}
            />

            <Dropdown
              menu={{ items: dropdownItems }}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <div className="user-avatar-trigger">
                <Avatar className="user-avatar">{getInitials(user?.name ?? '')}</Avatar>
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className={`navContent${isWealthRoute ? ' is-wealth' : ''}`} ref={navContentRef}>
        {maintenanceModeEnabled && (
          <div className="maintenance-banner" role="status" aria-live="polite">
            Maintenance mode
          </div>
        )}
        <div className="header">
          <div className="title-row">
            <div className="title">
              {location.pathname === '/home/customer-financial-plan'
                ? "Customer's Financial Plan"
                : title}
            </div>
            {isMobile && (
              <Dropdown
                menu={{ items: dropdownItems }}
                placement="bottomRight"
                arrow
                trigger={['click']}
              >
                <img
                  src={NotificationIcon}
                  alt="Notification"
                  className="mobile-header-notification-icon"
                />
              </Dropdown>
            )}
          </div>
          {isWealthRoute && (
            <div className="connect-button-container">
              {riaProfileLoaded && investorApiReadiness.identityReady && shouldShowApexActions && (
                <Button
                  type="primary"
                  className="brokerage-button connect-button"
                  onClick={handleOpenBrokerageAccount}
                >
                  Open an Account
                </Button>
              )}
            </div>
          )}
        </div>
        <FormModalProvider>
          <div className="outletContainer">
            {shouldShowPreparationState ? (
              <Result
                icon={<Spin size="large" />}
                title="Preparing your account"
                subTitle="We are securely preparing your account data. This should only take a moment."
              />
            ) : (
              <Outlet />
            )}
          </div>
        </FormModalProvider>
      </div>

      {
        <div className={`mobile-bottom-menu ${showBottomMenu ? 'visible' : 'hidden'}`}>
          {mobileMenuItems.map((item) => (
            <div
              key={item.key}
              className={`menu-item ${selectedKey === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.key)}
            >
              {item.icon}
            </div>
          ))}
        </div>
      }

      <PlaidLinkModal
        visible={plaidModalVisible}
        onClose={() => setPlaidModalVisible(false)}
        onSuccess={handlePlaidSuccess}
      />
    </div>
  );
};

export default Home;
